import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import SEO from '@/components/SEO';
import LoadingAnimation from '@/components/LoadingAnimation';
import { usePageLoading } from '@/hooks/usePageLoading';
import {
  HomeHeroSection,
  HomeAboutSection,
  HomeTechnologySection,
  HomePlatformsSection,
  HomePatentsSection,
  HomeManufacturingSection,
  HomeCapabilitiesSection
} from '@/features/home';

const Index = () => {
  const isLoading = usePageLoading(0);
  const location = useLocation();

  // Handle hash scrolling after loading completes
  useEffect(() => {
    if (!isLoading && location.hash) {
      const hash = location.hash.slice(1);
      let attempts = 0;
      const maxAttempts = 15;
      const retryInterval = 100;
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      
      const tryScroll = () => {
        const el = document.getElementById(decodeURIComponent(hash));
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          const heading = el.querySelector('h1, h2, h3, [role="heading"]') as HTMLElement | null;
          if (heading) {
            heading.setAttribute('tabindex', '-1');
            heading.focus({ preventScroll: true });
          }
        } else if (attempts < maxAttempts) {
          attempts++;
          timeoutId = setTimeout(tryScroll, retryInterval);
        }
      };
      
      tryScroll();
      return () => {
        if (timeoutId) clearTimeout(timeoutId);
      };
    }
  }, [isLoading, location.hash]);

  if (isLoading) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center">
          <LoadingAnimation />
        </div>
      </PageLayout>
    );
  }

  return (
    <div className="relative">
      <PageLayout>
        <SEO 
          title="Xenira - Pharmaceutical Drug Delivery Innovation" 
          description="Xenira: Advanced lipid-based drug delivery systems for enhanced bioavailability and therapeutic outcomes." 
          keywords={['drug delivery', 'lipid formulations', 'pharmaceutical technology', 'bioavailability enhancement', 'pharmaceutical development', 'manufacturing']} 
        />
        <HomeHeroSection />
        <HomeAboutSection />
        <HomeTechnologySection />
        <HomePlatformsSection />
        <HomePatentsSection />
        <HomeCapabilitiesSection />
        <HomeManufacturingSection />
      </PageLayout>
    </div>
  );
};

export default Index;